import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { InstallBaseFacade } from '../facades/install-base.facade';
import { combineLatest, mergeMap, Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';
import * as InstallBaseActions from '../actions/install-base.actions';
import { catchError, concatMap, map, switchMap, take } from 'rxjs/operators';
import { CustomerActions } from '../actions';
import { CustomerFacade } from '../facades/customer.facade';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { EFeatureToggles, EStoreFeatures } from '../configurations/common';
import { EInstalledBaseWorkflow } from '../models/installedbase.models';

@Injectable({
  providedIn: 'root',
})
export class InstallBaseEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly installBaseFacade: InstallBaseFacade = inject(InstallBaseFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  readonly initInstallBaseDataDataAfterLogin$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CustomerActions.CompanyUsersActionSuccess),
      mergeMap(() => combineLatest([
        this.customerFacade.isBusinessPartner(),
        this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW),
      ]).pipe(
        take(1),
        concatMap(([isBp, isPredefinedEquipmentFlowEnabled]) => {
          if (isPredefinedEquipmentFlowEnabled) {
            return [InstallBaseActions.InstallBaseProductsActionStartDataAction()];
          }

          if (isBp) {
            return [];
          } else {
            return [
              InstallBaseActions.InstallBaseSystemsActionStartDataAction(),
              InstallBaseActions.InstallBaseProductsActionStartDataAction(),
              InstallBaseActions.GetInstallBaseSystemsCpqDataActionStart(),
              InstallBaseActions.GetInstallBaseSystemsSparePartsDataActionStart(),
            ];
          }
        }),
      )),
    );
  });

  readonly GetInstallBaseSystemsData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(InstallBaseActions.InstallBaseSystemsActionStartDataAction),
      mergeMap(
        () => {
          const isCpqAvailable: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.CPQ);
          const isSparePartsAvailable: boolean = this.configurationFacade.isFeatureAvailable(EStoreFeatures.SPARE_PARTS);
          if (isCpqAvailable || isSparePartsAvailable) {
            // return an action with empty payload to ensure loading flag is canceled in application state
            return of(InstallBaseActions.SuccessGetInstallBaseSystemsDataAction({
              payload: {
                data: [],
                links: {self: ''},
              },
            }));
          } else {
            // "Regular" equipments are only for stores which do not support any "special" workflows
            // such as CPQ or Spare parts
            return this.installBaseFacade.shsEquipmentInstalledBaseSystemsStartLoadingData({})
              .pipe(
                map((payload) =>
                  InstallBaseActions.SuccessGetInstallBaseSystemsDataAction({payload}),
                ),
                catchError(error => of(InstallBaseActions.GetInstallBaseSystemsDataActionFailed({
                  error,
                }))),
              );
          }
        },
      ),
    ));

  readonly GetCpqInstallBaseSystemsData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(InstallBaseActions.GetInstallBaseSystemsCpqDataActionStart),
      switchMap(
        () => this.configurationFacade.isFeatureEnabled(EFeatureToggles.CPQ).pipe(
          take(1),
          switchMap((isCpqEnabled) => {
            if (isCpqEnabled) {
              return this.installBaseFacade.shsEquipmentInstalledBaseSystemsStartLoadingData({
                workflow: EInstalledBaseWorkflow.CPQ,
              }).pipe(
                map((payload) =>
                  InstallBaseActions.GetInstallBaseSystemsCpqDataActionSuccess({payload}),
                ),
                catchError(error => of(InstallBaseActions.GetInstallBaseSystemsCpqDataActionFailed({
                  error,
                }))),
              );
            } else {
              // return an action with empty payload to ensure loading flag is canceled in application state
              return of(InstallBaseActions.GetInstallBaseSystemsCpqDataActionSuccess({
                payload: {
                  data: [],
                  links: {self: ''},
                },
              }));
            }
          }),
        ),
      ),
    ));

  readonly GetSparePartsInstallBaseSystemsData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(InstallBaseActions.GetInstallBaseSystemsSparePartsDataActionStart),
      switchMap(
        () => this.configurationFacade.isFeatureEnabled(EFeatureToggles.SPARE_PARTS).pipe(
          take(1),
          switchMap((isSparePartsEnabled) => {
            if (isSparePartsEnabled) {
              return this.installBaseFacade.shsEquipmentInstalledBaseSystemsStartLoadingData({
                workflow: EInstalledBaseWorkflow.SPARE_PARTS,
              }).pipe(
                map((payload) =>
                  InstallBaseActions.GetInstallBaseSystemsSparePartsDataActionSuccess({payload}),
                ),
                catchError(error => of(InstallBaseActions.GetInstallBaseSystemsSparePartsDataActionFailed({
                  error,
                }))),
              );
            } else {
              // return an action with empty payload to ensure loading flag is canceled in application state
              return of(InstallBaseActions.GetInstallBaseSystemsSparePartsDataActionSuccess({
                payload: {
                  data: [],
                  links: {self: ''},
                },
              }));
            }
          }),
        ),
      ),
    ));

  readonly GetInstallBaseProductsData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(InstallBaseActions.InstallBaseProductsActionStartDataAction),
      mergeMap(
        () => this.installBaseFacade.installedBaseProductsStartLoadingData()
          .pipe(
            map((product) =>
              InstallBaseActions.SuccessGetInstallBaseProductsDataAction({product}),
            ),
            catchError(error => of(InstallBaseActions.GetInstallBaseProductsDataActionFailed({error}))),
          ),
      ),
    ));
}
